import React, { Dispatch, SetStateAction } from 'react';

import { css } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';
import { TRACKING_EVENT_ACTION_SUBJECT_IDS } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/views/user-seg-bxp-ui/constants.ts';

import { JiraTemplateChoice } from '../../../hooks/use-jira-template-choice.tsx';
import { UserSegmentationTeamTypes } from '../../../models/user-segmentation';
import { TemplateOptionMetadata } from '../template-definitions';

export interface CardProps {
	imageSrc: string;
	mobileImgSrc: string;
	title: string;
	subtext: string;
	templateType: JiraTemplateChoice;
	templateId: string;
	setSelectedTemplate: Dispatch<SetStateAction<JiraTemplateChoice>>;
	setSelectedTemplateId: Dispatch<SetStateAction<string | undefined>>;
	selectedCardTitle: string | undefined;
	setSelectedCardTitle: Dispatch<SetStateAction<string | undefined>>;
	teamType: UserSegmentationTeamTypes;
	templateMetadata?: TemplateOptionMetadata;
	setSelectedTemplateMetadata: Dispatch<SetStateAction<TemplateOptionMetadata | undefined>>;
	/** Rendered below the card description, e.g. a "Powered by" section. */
	footer?: React.ReactNode;
	/** Extra attributes merged into the card click analytics event. */
	analyticsAttributes?: Record<string, unknown>;
}

const cardBorderStyle = css({
	margin: 0,
	padding: '0px',
	borderRadius: 'var(--rounding, 8px)',
	border: 'none',
	background: wacToken('color.neutral.light0'),
	overflow: 'hidden',
	display: 'flex',
	flexDirection: 'row',
	maxWidth: '463px',
	alignItems: 'center',
	cursor: 'pointer',
	['&:hover']: {
		boxShadow:
			// eslint-disable-next-line local/ensure-wac-token-usage
			'0px 1px 1px 0px rgba(9, 30, 66, 0.25), 0px 0px 1px 0px rgba(9, 30, 66, 0.31)',
	},
	[media.above.md]: {
		flexDirection: 'column',
		justifyContent: 'flex-start',
		maxWidth: 'none',
		minHeight: '333px',
		flex: 1,
	},
});

const cardTitleStyle = css({
	fontFamily: token('font.family.heading'),
	color: token('color.text'),
	fontSize: '1.25rem',
	fontWeight: token('font.weight.bold'),
	lineHeight: '1.75rem',
	margin: 0,
	[media.above.md]: {
		fontSize: '20px',
		lineHeight: '28px',
	},
});

const cardDescriptionVisualUpdateStyle = css({
	fontFamily: token('font.family.body'),
	fontWeight: token('font.weight.regular'),
	// eslint-disable-next-line local/ensure-wac-token-usage
	fontSize: '1rem',
	// eslint-disable-next-line local/ensure-wac-token-usage
	lineHeight: '1.5rem',
	margin: 0,
});

const cardBorderUnselectedStyle = css({
	outline: `1px solid ${token('color.background.accent.gray.subtlest')}`,
	borderRadius: '8px',
});

const cardBorderSelectedStyle = css({
	outline: `2px solid ${token('color.link')}`,
	borderRadius: '8px',
});

const titleContainerStyle = css({
	display: 'flex',
	alignItems: 'center',
});

const textContainerStyle = css({
	alignItems: 'flex-start',
	fontFamily: wacToken('font.charlie.text'),
	padding: token('space.300'),
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.050'),
	justifyContent: 'space-between',
	textAlign: 'start',
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['& h3, & p']: {
		margin: 0,
		width: '100%',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['& p']: {
		color: token('color.text'),
	},
	[media.above.md]: {
		padding: token('space.300'),
		justifyContent: 'flex-start',
		flexGrow: 1,
	},
});

const imgStyle = css({
	display: 'none',
	[media.above.md]: {
		display: 'block',
		width: '100%',
		height: 'auto',
		flexBasis: 'auto',
	},
});

const mobileImgStyle = css({
	width: '80px',
	height: '100px',
	alignSelf: 'center',
	[media.above.md]: {
		display: 'none',
	},
});

const desktopImageContainer = css({
	display: 'none',
	[media.above.md]: {
		display: 'block',
		marginBlockEnd: token('space.100'),
	},
});

const Card: React.FC<CardProps> = ({
	imageSrc,
	mobileImgSrc,
	title,
	subtext,
	setSelectedTemplate,
	templateType,
	templateId,
	setSelectedTemplateId,
	setSelectedCardTitle,
	selectedCardTitle,
	teamType,
	templateMetadata,
	setSelectedTemplateMetadata,
	footer,
	analyticsAttributes,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isSelected = selectedCardTitle === title;

	return (
		<button
			css={[cardBorderStyle, isSelected ? cardBorderSelectedStyle : cardBorderUnselectedStyle]}
			onClick={() => {
				createAnalyticsEvent({
					eventType: 'ui',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: TRACKING_EVENT_ACTION_SUBJECT_IDS.templateChoiceCard,
					attributes: {
						atlasTicket: 'ATLAS-61649',
						teamType,
						templateType,
						templateId,
						templateName: title,
						...analyticsAttributes,
					},
				}).fire();

				setSelectedTemplate(templateType);
				setSelectedTemplateId(templateId);
				setSelectedCardTitle(title);
				setSelectedTemplateMetadata(templateMetadata);
			}}
		>
			<div css={desktopImageContainer}>
				{/* eslint-disable-next-line no-restricted-syntax */}
				<img src={imageSrc} alt="" css={imgStyle} />
			</div>
			{/* eslint-disable-next-line no-restricted-syntax */}
			<img src={mobileImgSrc} alt="" css={mobileImgStyle} />
			<div css={textContainerStyle}>
				<div css={titleContainerStyle}>
					<h3 css={cardTitleStyle}>{title}</h3>
				</div>
				<p css={cardDescriptionVisualUpdateStyle}>{subtext}</p>
				{footer}
			</div>
		</button>
	);
};

export default Card;
